import type { QueryClient } from '@tanstack/react-query';

/**
 * An order changed, so everything derived from orders is now wrong.
 *
 * This exists because the bake sheet was forgotten. Six places created or
 * edited an order and each refreshed the order list beside it; none of them
 * knew a production sheet had since been built on the same data, so a loaf
 * ordered at the counter never reached the kitchen's list. Adding a seventh
 * line to six call sites would have worked until the eighth site was written.
 *
 * Anything that creates, edits, cancels or reinstates an order calls this
 * instead of naming keys, so the next screen built on orders is added here once
 * rather than hunted for later.
 */
export function ordersChanged(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: ['orders'] });
  // Every date, not just today: an order can be moved to another day.
  queryClient.invalidateQueries({ queryKey: ['bake-sheet'] });
  queryClient.invalidateQueries({ queryKey: ['delivery-route'] });
  bakeSheetChanged(queryClient);
}

/**
 * The sheet itself changed — a count typed, a restock figure set.
 *
 * Separate from `ordersChanged` because the sheet is edited directly as well as
 * through orders, and both paths change what the shop can offer a customer.
 * The list of goods quoted in a chat is built from that same Available column,
 * so it goes stale the moment either one moves.
 */
export function bakeSheetChanged(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: ['bake-sheet'] });
  queryClient.invalidateQueries({ queryKey: ['available-message'] });
}
